import type {
  DraftMessageId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { RedoMerchantRpcClient } from "@redotech/merchant-sdk/rpc/client";
import type { RedoMerchantClient } from "@redotech/redo-merchant-app-common/client";
import { getTeamReturns } from "@redotech/redo-merchant-app-common/client/team";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import type { MerchantAppReturn } from "@redotech/redo-model/return";
import type { SupportTicketDraftsApi } from "@redotech/redo-support-v2/drafts/support-ticket-drafts-collection";
import type { SupportTicketListApi } from "@redotech/redo-support-v2/query/support-ticket-list-collection";
import type {
  OrderWithPickStatus,
  SupportTicketRelatedRecordsApi,
} from "@redotech/redo-support-v2/query/support-ticket-related-records-collections";
import type {
  AccountTagsResponse,
  SupportTicketTagsApi,
} from "@redotech/redo-support-v2/query/support-ticket-tags-collections";
import type { SupportTicketTranscriptApi } from "@redotech/redo-support-v2/query/support-ticket-transcript-collections";
import { catchApiErrors } from "./error-handling";

export interface CreateMerchantSupportTicketDataSourceOptions {
  effectApi: EffectApiContext;
  rpcClient?: RedoMerchantRpcClient;
  merchantClient?: RedoMerchantClient;
}

export interface MerchantSupportTicketDataSource {
  list: SupportTicketListApi;
  transcript: SupportTicketTranscriptApi;
  tags: SupportTicketTagsApi;
  relatedRecords: SupportTicketRelatedRecordsApi;
  drafts: SupportTicketDraftsApi;
  getSupportTicketSummary(
    supportTicketId: SupportTicketId,
  ): Promise<{ summary: string | null }>;
}

export function createMerchantSupportTicketDataSource({
  effectApi,
  rpcClient,
  merchantClient,
}: CreateMerchantSupportTicketDataSourceOptions): MerchantSupportTicketDataSource {
  const client = effectApi.getClient();

  return {
    list: {
      getSupportTicket: (supportTicketId) =>
        effectApi.runPromise(
          catchApiErrors(
            client.supportTicket.getSupportTicket({
              path: { id: supportTicketId },
            }),
          ),
        ),
      listSupportTickets: (urlParams) =>
        effectApi.runPromise(
          catchApiErrors(
            client.supportTicket.listSupportTickets({ urlParams }),
          ),
        ),
      updateSupportTicket: (supportTicketId, payload) =>
        effectApi
          .runPromise(
            catchApiErrors(
              client.supportTicket.updateSupportTicket({
                path: { id: supportTicketId },
                payload,
              }),
            ),
          )
          .then(() => undefined),
    },
    transcript: {
      getThreads: (supportTicketId) =>
        effectApi.runPromise(
          catchApiErrors(
            client.supportTicket.getSupportTicketThreads({
              path: { id: supportTicketId },
              urlParams: { limit: 200 },
            }),
          ),
        ),
      getMessages: (supportTicketId) =>
        effectApi.runPromise(
          catchApiErrors(
            client.supportTicket.getSupportTicketMessages({
              path: { id: supportTicketId },
              urlParams: { limit: 200 },
            }),
          ),
        ),
      getTimelineEvents: (supportTicketId) =>
        effectApi.runPromise(
          catchApiErrors(
            client.supportTicket.getSupportTicketTimelineEvents({
              path: { id: supportTicketId },
            }),
          ),
        ),
    },
    tags: {
      getAccountTags: async (): Promise<AccountTagsResponse> => {
        const result = await effectApi.runPromise(
          catchApiErrors(client.tag.getAccountTags({})),
        );
        return { tags: [...result.tags], tagGroups: [...result.tagGroups] };
      },
    },
    relatedRecords: {
      getOrdersByEmail: async (email) => {
        if (!rpcClient) {
          throw new Error(
            "Related order lookup requires a merchant RPC client",
          );
        }
        const result = await rpcClient.getTeamOrdersByEmail({ email });
        return result.orders as OrderWithPickStatus[];
      },
      getReturnsByEmail: async (email): Promise<MerchantAppReturn[]> => {
        if (!merchantClient) {
          throw new Error("Related return lookup requires a merchant client");
        }
        const result = await getTeamReturns(merchantClient, { email });
        return result as MerchantAppReturn[];
      },
    },
    drafts: {
      getDrafts: (supportTicketId: SupportTicketId) =>
        effectApi.runPromise(
          catchApiErrors(
            client.supportTicket.getSupportTicketDrafts({
              path: { id: supportTicketId },
            }),
          ),
        ),
      saveDraft: (supportTicketId, payload) =>
        effectApi
          .runPromise(
            catchApiErrors(
              client.supportTicket.saveSupportTicketDraft({
                path: { id: supportTicketId },
                payload,
              } as Parameters<
                typeof client.supportTicket.saveSupportTicketDraft
              >[0]),
            ),
          )
          .then(() => undefined),
      deleteDraft: (supportTicketId, draftId: DraftMessageId) =>
        effectApi.runPromise(
          catchApiErrors(
            client.supportTicket.deleteSupportTicketDraft({
              path: { id: supportTicketId, draftId },
            }),
          ),
        ),
    },
    getSupportTicketSummary: (supportTicketId: SupportTicketId) =>
      effectApi.runPromise(
        catchApiErrors(
          client.supportTicket.getSupportTicketSummary({
            path: { id: supportTicketId },
          }),
        ),
      ),
  };
}
